import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Bell } from 'lucide-react';
import { useAuth } from './useAuth';
import { requestFcmToken, onForegroundMessage, isFcmConfigured } from '@/firebase';
import { notificationService } from '@/services';

export const FCM_TOKEN_KEY = 'edc.fcmToken';

// Registers the device for push after login and shows foreground toasts.
// No-op when Firebase isn't configured or the browser doesn't support push.
export function usePushNotifications() {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || !isFcmConfigured) return undefined;

    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      const token = await requestFcmToken();
      if (!cancelled && token) {
        try {
          await notificationService.registerToken(token);
          localStorage.setItem(FCM_TOKEN_KEY, token);
        } catch {
          /* ignore registration failures */
        }
      }

      unsubscribe = await onForegroundMessage((n) => {
        qc.invalidateQueries({ queryKey: ['notifications'] });
        toast.custom(
          (t) => (
            <button
              onClick={() => {
                toast.dismiss(t.id);
                if (n.link) navigate(n.link);
              }}
              className="flex items-start gap-3 max-w-sm w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-soft rounded-xl px-4 py-3 text-left"
            >
              <span className="w-8 h-8 rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300 grid place-items-center shrink-0">
                <Bell size={16} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold truncate">{n.title}</span>
                {n.body && <span className="block text-xs text-slate-500 line-clamp-2">{n.body}</span>}
              </span>
            </button>
          ),
          { duration: 6000 }
        );
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [isAuthenticated, qc, navigate]);
}
